import * as React from "react";

import { cn } from "@athena/ui/lib/utils";

export interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
    /**
     * Classes for the scroll container around the table, where its card edge lives.
     *
     * `className` reaches the `<table>`, which leaves the border and the corner radius below
     * unreachable — and a table nested inside a card that already has both draws them twice. This
     * is how a caller turns those off without the wrapper's scroll behavior going with them.
     */
    wrapperClassName?: string;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(({ className, wrapperClassName, ...props }, ref) => (
    /*
     * Horizontal only.
     *
     * `overflow-auto` makes this a scroll container on *both* axes. A wide table then owns the
     * wheel: side-scrolling works and vertical scrolling does nothing, because the gesture is
     * delivered to an element that has somewhere to go on X and the browser does not chain it
     * to the page. Rosters are wide enough to hit this on every screen narrower than the table.
     *
     * `overflow-y-hidden` rather than leaving it to compute: with `overflow-x` set to anything
     * but `visible`, a `visible` on the other axis computes to `auto`, so asking for horizontal
     * alone is not a thing CSS lets you say. Nothing clips — the wrapper is as tall as the
     * table, and the vertical scrolling is the document's, which is also what `useVirtualRows`
     * measures against.
     */
    <div
        className={cn(
            "bg-card border-border w-full overflow-x-auto overflow-y-hidden rounded-2xl border",
            wrapperClassName,
        )}
    >
        <table ref={ref} className={cn("w-full caption-bottom text-sm", className)} {...props} />
    </div>
));
Table.displayName = "Table";

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
    ({ className, ...props }, ref) => (
        <thead
            ref={ref}
            className={cn("bg-muted/40 [&_tr]:border-b [&_tr]:hover:bg-transparent", className)}
            {...props}
        />
    ),
);
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
    ({ className, ...props }, ref) => (
        <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />
    ),
);
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
    ({ className, ...props }, ref) => (
        <tfoot
            ref={ref}
            className={cn("bg-muted/50 border-t font-medium [&>tr]:last:border-b-0", className)}
            {...props}
        />
    ),
);
TableFooter.displayName = "TableFooter";

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
    clickable?: boolean;
}

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
    ({ className, clickable = false, ...props }, ref) => (
        <tr
            ref={ref}
            className={cn(
                "data-[state=selected]:bg-muted border-b transition-colors",
                clickable && "hover:bg-muted/30 hover:cursor-pointer",
                className,
            )}
            {...props}
        />
    ),
);
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
    ({ className, ...props }, ref) => (
        <th
            ref={ref}
            className={cn(
                "text-muted-foreground h-10 px-5 text-left align-middle font-mono text-xs font-medium tracking-wider uppercase [&:has([role=checkbox])]:pr-0",
                className,
            )}
            {...props}
        />
    ),
);
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
    ({ className, ...props }, ref) => (
        <td ref={ref} className={cn("px-5 py-4 align-middle [&:has([role=checkbox])]:pr-0", className)} {...props} />
    ),
);
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
    ({ className, ...props }, ref) => (
        <caption ref={ref} className={cn("text-muted-foreground mt-4 text-sm", className)} {...props} />
    ),
);
TableCaption.displayName = "TableCaption";

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
